import { useState } from "react";
import { api } from "./api";
import Logo, { useBranding } from "./components/common/Logo";

const BASE = "https://support.gs-group.fr/api";

// Palette de marque GS Group (indépendante du thème choisi dans l'appli — cf. favicon.svg) :
// dégradé bleu nuit + accent cyan (dôme de vidéosurveillance).
const NAVY_DARK = "#071020";
const NAVY_MID  = "#0d2444";
const NAVY_LIGHT= "#1a3d6e";
const CYAN      = "#4ab8d8";

const CSS_ANIM = `
  @keyframes login-spin { to { transform: rotate(360deg); } }
  @keyframes login-glow { 0%,100%{ opacity:.55; transform:scale(1); } 50%{ opacity:.85; transform:scale(1.08); } }
  @keyframes login-in { from{ opacity:0; transform:translateY(10px) scale(.98); } to{ opacity:1; transform:translateY(0) scale(1); } }
  .login-left-panel { display:flex; }
  .login-right-panel { width:50%; }
  @media (max-width: 900px) {
    .login-left-panel { display:none; }
    .login-right-panel { width:100%; }
  }
`;

export default function Login({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [mfaStep, setMfaStep]   = useState(false);
  // Challenge MFA intermédiaire (≠ JWT de session) : en mémoire uniquement, purgé dès la fin du flux.
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode]         = useState(["","","","","",""]);
  const [mfaError, setMfaError] = useState("");
  const [mfaLoading, setMfaLoad] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [pwdFocus, setPwdFocus]     = useState(false);
  const [btnHover, setBtnHover]     = useState(false);
  const [btnDown, setBtnDown]       = useState(false);
  const branding = useBranding();

  const handleLogin = async () => {
    if (!email||!password) return setError("Remplissez tous les champs");
    setLoading(true); setError("");
    try {
      const res = await api.login(email, password);
      if (res.mfa_required) {
        if (!res.mfa_token) { setError("Erreur de connexion"); }
        else { setMfaToken(res.mfa_token); setMfaError(""); setCode(["","","","","",""]); setMfaStep(true); }
      } else if (res.token) {
        localStorage.setItem("nexo_token", res.token);
        localStorage.setItem("nexo_user", JSON.stringify(res.user));
        onLogin(res.user);
      } else {
        setError(res.error || "Identifiants incorrects");
      }
    } catch(e) { setError("Erreur de connexion"); }
    setLoading(false);
  };

  // Abandon / fin du flux MFA : le challenge n'est jamais conservé.
  const resetMfa = (loginError = "") => {
    setMfaToken(null);
    setMfaStep(false);
    setCode(["","","","","",""]);
    setMfaError("");
    setError(loginError);
  };

  const handleMFA = async (fullCode) => {
    if (!mfaToken || mfaLoading) return;
    setMfaLoad(true); setMfaError("");
    try {
      const r = await fetch(`${BASE}/auth/verify-mfa`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({mfa_token:mfaToken, code:fullCode})
      });
      const res = await r.json().catch(()=>({}));
      setMfaLoad(false);
      if (r.status===429) {
        resetMfa("Trop de tentatives, veuillez vous reconnecter");
      } else if (r.status===401 && res.error==="Code invalide") {
        // Mauvais code : le challenge reste valable, nouvelle tentative possible.
        setMfaError("Code invalide");
        setCode(["","","","","",""]);
        document.getElementById("mfa-0")?.focus();
      } else if (r.status===401) {
        resetMfa("Session de vérification invalide ou expirée, veuillez vous reconnecter");
      } else if (r.ok && res.token) {
        setMfaToken(null);
        localStorage.setItem("nexo_token", res.token);
        localStorage.setItem("nexo_user", JSON.stringify(res.user));
        onLogin(res.user);
      } else {
        setMfaError(res.error||"Code invalide");
        setCode(["","","","","",""]);
        document.getElementById("mfa-0")?.focus();
      }
    } catch(e) { setMfaLoad(false); setMfaError("Erreur de connexion"); }
  };

  const handleCodeChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const n = [...code]; n[i] = val.slice(-1); setCode(n);
    if (val && i<5) document.getElementById(`mfa-${i+1}`)?.focus();
    if (n.every(c=>c) && val) handleMFA(n.join(""));
  };

  const handleCodeKey = (i, e) => {
    if (e.key==="Backspace" && !code[i] && i>0) document.getElementById(`mfa-${i-1}`)?.focus();
  };

  const mkInp = focused => ({
    background:"#f8fafc", border:`1.5px solid ${focused?NAVY_MID:"#e2e8f0"}`, borderRadius:12,
    padding:"11px 13px", color:"#0f172a", fontSize:14, outline:"none", width:"100%",
    boxShadow:focused?`0 0 0 3px ${NAVY_MID}22`:"none", transition:"border-color .15s, box-shadow .15s",
  });
  const btn = {
    padding:"14px", borderRadius:14, border:"none", color:"#fff", fontSize:15, fontWeight:700,
    cursor:loading?"default":"pointer", width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:9,
    background:`linear-gradient(135deg,${NAVY_MID},${NAVY_DARK})`,
    boxShadow:btnHover&&!loading?`0 8px 24px ${NAVY_MID}55`:`0 4px 16px ${NAVY_MID}30`,
    transform:btnDown&&!loading?"scale(0.98)":btnHover&&!loading?"translateY(-1px)":"none",
    transition:"box-shadow .18s ease, transform .12s ease", opacity:loading?0.85:1,
  };

  const BULLETS = [
    { ic:"📄", txt:"Accès centralisé à vos documents et informations" },
    { ic:"🛡️", txt:"Assistance technique et juridique dédiée" },
    { ic:"🔒", txt:"Stockage sécurisé de vos données" },
  ];

  return (
    <div style={{minHeight:"100vh",display:"flex",fontFamily:"'Outfit',sans-serif"}}>
      <style>{CSS_ANIM}</style>

      {/* ── Côté gauche : identité de marque (bleu nuit) ── */}
      <div className="login-left-panel" style={{width:"50%",minHeight:"100vh",position:"relative",overflow:"hidden",flexDirection:"column",justifyContent:"space-between",padding:"40px 56px",background:`linear-gradient(160deg,${NAVY_LIGHT} 0%,${NAVY_DARK} 65%)`,color:"#fff",boxSizing:"border-box"}}>
        <div style={{position:"absolute",top:"-10%",left:"-10%",width:420,height:420,borderRadius:"50%",background:CYAN,filter:"blur(140px)",opacity:.25,animation:"login-glow 8s ease-in-out infinite"}}/>
        <div style={{position:"absolute",bottom:"-15%",right:"-10%",width:360,height:360,borderRadius:"50%",background:"#8b5cf6",filter:"blur(140px)",opacity:.18,animation:"login-glow 9s ease-in-out infinite 1.2s"}}/>

        {/* Logo */}
        <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",gap:12}}>
          <Logo variant="login"/>
          <span style={{fontSize:15,fontWeight:900,letterSpacing:"2px",color:CYAN}}>{(branding?.company_name||"GS GROUP").toUpperCase()}</span>
        </div>

        {/* Titre + puces */}
        <div style={{position:"relative",zIndex:1,maxWidth:440}}>
          <div style={{fontSize:36,fontWeight:800,lineHeight:1.2,letterSpacing:"-0.5px",marginBottom:18}}>
            Ensemble, sécurisons votre savoir
          </div>
          <div style={{fontSize:15,color:"rgba(255,255,255,0.72)",lineHeight:1.6,marginBottom:28}}>
            Votre espace collaborateur centralise le suivi de vos interventions, vos documents et votre assistance GS Group.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {BULLETS.map((b,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:14}}>
                <div style={{width:38,height:38,flexShrink:0,borderRadius:11,background:"rgba(255,255,255,0.08)",border:`1px solid ${CYAN}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17}}>{b.ic}</div>
                <div style={{fontSize:14,color:"rgba(255,255,255,0.88)",fontWeight:500}}>{b.txt}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Mention sécurité */}
        <div style={{position:"relative",zIndex:1,fontSize:12,color:"rgba(255,255,255,0.5)",display:"flex",alignItems:"center",gap:8}}>
          🔒 Connexion chiffrée · Accès réservé aux collaborateurs
        </div>
      </div>

      {/* ── Côté droit : formulaire ── */}
      <div className="login-right-panel" style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"#f4f6fb",padding:24,boxSizing:"border-box"}}>
        <div style={{width:400,maxWidth:"100%",background:"#fff",borderRadius:20,padding:40,boxShadow:"0 24px 60px rgba(15,23,42,0.12),0 4px 16px rgba(15,23,42,0.06)",animation:"login-in .45s cubic-bezier(.16,1,.3,1)"}}>

          {/* Header */}
          <div style={{marginBottom:28}}>
            <div style={{fontSize:24,fontWeight:800,color:"#0f172a",letterSpacing:"-0.5px"}}>
              {mfaStep ? "Vérification" : "Connexion"}
            </div>
            <div style={{fontSize:13.5,color:"#64748b",marginTop:6}}>
              {mfaStep ? "Vérification en deux étapes" : "Accéder à votre espace GS Group"}
            </div>
          </div>

          {/* LOGIN */}
          {!mfaStep&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {error&&<div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:10,padding:"10px 14px",fontSize:13,color:"#dc2626",textAlign:"center"}}>⚠ {error}</div>}
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.7px",display:"block",marginBottom:6}}>Adresse e-mail</label>
                <input value={email} onChange={e=>setEmail(e.target.value)} type="email" placeholder="votre@email.com"
                  onFocus={()=>setEmailFocus(true)} onBlur={()=>setEmailFocus(false)} style={mkInp(emailFocus)}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.7px",display:"block",marginBottom:6}}>Mot de passe</label>
                <div style={{position:"relative"}}>
                  <input value={password} onChange={e=>setPassword(e.target.value)} type={showPwd?"text":"password"} placeholder="••••••••"
                    onFocus={()=>setPwdFocus(true)} onBlur={()=>setPwdFocus(false)}
                    onKeyDown={e=>e.key==="Enter"&&handleLogin()} style={{...mkInp(pwdFocus),paddingRight:42}}/>
                  <button type="button" onClick={()=>setShowPwd(p=>!p)} tabIndex={-1}
                    aria-label={showPwd?"Masquer le mot de passe":"Afficher le mot de passe"}
                    style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:16,padding:8,lineHeight:1}}>
                    {showPwd?"🙈":"👁"}
                  </button>
                </div>
              </div>
              <button onClick={handleLogin} disabled={loading} style={btn}
                onMouseEnter={()=>setBtnHover(true)} onMouseLeave={()=>{setBtnHover(false);setBtnDown(false);}}
                onMouseDown={()=>setBtnDown(true)} onMouseUp={()=>setBtnDown(false)}>
                {loading
                  ? <><span style={{width:16,height:16,border:"2px solid rgba(255,255,255,.4)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"login-spin .7s linear infinite"}}/> Connexion...</>
                  : <>Se connecter</>}
              </button>
              <div style={{textAlign:"center",fontSize:11.5,color:"#94a3b8",marginTop:2}}>
                Accès réservé aux collaborateurs GS Group
              </div>
            </div>
          )}

          {/* MFA */}
          {mfaStep&&(
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:12,padding:"12px 14px",fontSize:13,color:"#475569",textAlign:"center",lineHeight:1.6}}>
                🔐 Entrez le code à 6 chiffres<br/>
                <strong style={{color:"#0f172a"}}>Google Authenticator · Authy · 1Password</strong>
              </div>

              <div style={{display:"flex",gap:8,justifyContent:"center"}}>
                {code.map((c,i)=>(
                  <input key={i} id={`mfa-${i}`}
                    value={c} onChange={e=>handleCodeChange(i,e.target.value)} onKeyDown={e=>handleCodeKey(i,e)}
                    maxLength={1} type="text" inputMode="numeric"
                    style={{width:48,height:58,textAlign:"center",fontSize:26,fontWeight:800,fontFamily:"monospace",
                      background:"#f8fafc",border:`2px solid ${c?NAVY_MID:"#e2e8f0"}`,
                      borderRadius:12,color:"#0f172a",outline:"none"}}/>
                ))}
              </div>

              {mfaError&&<div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:10,padding:"10px",fontSize:13,color:"#dc2626",textAlign:"center"}}>{mfaError}</div>}
              {mfaLoading&&<div style={{textAlign:"center",color:"#64748b",fontSize:13}}>⏳ Vérification...</div>}

              <button onClick={()=>resetMfa()}
                style={{...btn,background:"transparent",border:"1px solid #e2e8f0",color:"#64748b",boxShadow:"none"}}>
                ← Retour
              </button>
            </div>
          )}
        </div>

        {/* Accès Support Technique */}
        <a href="mailto:support@gs-group.fr" style={{marginTop:18,width:400,maxWidth:"100%",boxSizing:"border-box",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"13px",borderRadius:14,background:"#fff",border:"1.5px solid #e2e8f0",color:"#334155",fontSize:13.5,fontWeight:700,textDecoration:"none",boxShadow:"0 4px 14px rgba(15,23,42,0.05)"}}>
          🔧 Support Technique
        </a>
      </div>
    </div>
  );
}
